/**
 * Public Client Portal Hub — a client's home page listing their quotes + job progress.
 * NO auth — gated by long-lived HMAC `?t=` token sent in the welcome email.
 */
import { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import DesignVisual from "@/components/DesignVisual";
import Blueprint3DView from "@/components/Blueprint3DView";
import PortalARModal from "@/components/PortalARModal";
import { Loader2, AlertCircle, FileText, HardHat, ChevronRight, Phone, Mail, Palette, CheckCircle2, Circle, CalendarDays, Wind, RotateCcw, Box, PencilLine, Smartphone } from "lucide-react";

const API_ROOT = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtD = (d) => new Date(d).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });

const fmt = (n) =>
  n == null ? "" : new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", minimumFractionDigits: 2 }).format(n);

const STATUS_STYLE = {
  sent: "bg-cyan-50 text-cyan-700 border-cyan-300",
  viewed: "bg-cyan-50 text-cyan-700 border-cyan-300",
  accepted: "bg-emerald-50 text-emerald-700 border-emerald-300",
  declined: "bg-rose-50 text-rose-700 border-rose-300",
  rejected: "bg-rose-50 text-rose-700 border-rose-300",
};

export default function ClientPortalHome() {
  const { cid } = useParams();
  const [params] = useSearchParams();
  const [lightbox, setLightbox] = useState(null); // { url, caption, stage }
  const token = params.get("t");
  const isStaffPreview = params.get("preview") === "1"; // profile Preview button — don't log a staff visit
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [visitDone, setVisitDone] = useState(() => {
    try { return sessionStorage.getItem(`ccfi_portal_visited_${cid}`) === "1"; } catch { return true; }
  });
  const [visitorName, setVisitorName] = useState(() => {
    try { return localStorage.getItem("ccfi_portal_visitor") || ""; } catch { return ""; }
  });
  const [savingVisit, setSavingVisit] = useState(false);

  const submitVisit = async () => {
    const name = visitorName.trim();
    if (!name || savingVisit) return;
    setSavingVisit(true);
    try {
      await axios.post(`${API_ROOT}/public/portal/client/${cid}/visit?t=${encodeURIComponent(token)}`, { name });
    } catch { /* never lock a client out if logging fails */ }
    try {
      sessionStorage.setItem(`ccfi_portal_visited_${cid}`, "1");
      localStorage.setItem("ccfi_portal_visitor", name);
    } catch { /* private browsing */ }
    setSavingVisit(false);
    setVisitDone(true);
  };

  useEffect(() => {
    if (!token) { setState({ loading: false, error: "Invalid link — token missing", data: null }); return; }
    (async () => {
      try {
        const { data } = await axios.get(`${API_ROOT}/public/portal/client/${cid}?t=${token}`);
        setState({ loading: false, error: null, data });
      } catch (e) {
        const code = e?.response?.status;
        const msg = code === 403
          ? "This link has expired or is invalid. Please contact CCFI for a fresh link."
          : code === 404 ? "Client record not found." : "Could not load your portal. Please try again shortly.";
        setState({ loading: false, error: msg, data: null });
      }
    })();
  }, [cid, token]);

  if (state.loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <Loader2 className="animate-spin text-cyan-600" size={28} />
      </div>
    );
  }
  if (state.error) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-6">
        <Card className="rounded-sm p-8 max-w-md text-center" data-testid="portal-home-error">
          <AlertCircle className="mx-auto text-rose-500 mb-3" size={28} />
          <p className="text-slate-700 text-sm">{state.error}</p>
          <p className="text-xs text-slate-400 mt-4">Central Coast Fencing Industries · 02 4324 1441</p>
        </Card>
      </div>
    );
  }

  const { client, quotes, jobs, designs = [] } = state.data;

  if (!visitDone && !isStaffPreview) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-6" data-testid="portal-name-gate">
        <Card className="rounded-sm p-8 max-w-md w-full">
          <div className="flex items-center gap-3 mb-4">
            <img src="/ccfi-logo.png?v=4" alt="CCFI" className="w-11 h-11 object-contain shrink-0" />
            <div>
              <div className="text-cyan-600 text-[10px] tracking-[0.18em] uppercase font-bold">Central Coast Fencing Industries</div>
              <div className="font-display text-lg font-semibold text-slate-900">{client.name} — client portal</div>
            </div>
          </div>
          <p className="text-sm text-slate-600">Welcome! Before you continue, let us know who's viewing today — just a first name is fine.</p>
          <form onSubmit={(e) => { e.preventDefault(); submitVisit(); }} className="mt-4 space-y-3">
            <input
              type="text" autoFocus list="portal-visitor-names" autoComplete="given-name"
              value={visitorName} onChange={(e) => setVisitorName(e.target.value)}
              placeholder="Your first name"
              className="w-full h-11 border border-slate-300 rounded-sm px-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
              data-testid="portal-visitor-name-input"
            />
            <datalist id="portal-visitor-names">
              {visitorName.trim() === "" ? null : null}
              {(() => { try { const last = localStorage.getItem("ccfi_portal_visitor"); return last ? <option value={last} /> : null; } catch { return null; } })()}
            </datalist>
            <Button type="submit" disabled={!visitorName.trim() || savingVisit}
              className="w-full h-11 rounded-sm bg-cyan-600 hover:bg-cyan-700 text-white font-semibold"
              data-testid="portal-visitor-continue-btn">
              {savingVisit ? <Loader2 size={15} className="animate-spin mr-2" /> : null} Continue to your portal
            </Button>
          </form>
          <p className="text-[11px] text-slate-400 mt-4">This helps us know who to follow up with. Central Coast Fencing Industries · 02 4324 1441</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100" data-testid="client-portal-home">
      <header className="bg-slate-900 text-white">
        <div className="max-w-3xl mx-auto px-5 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/ccfi-logo.png?v=4" alt="CCFI" className="w-12 h-12 object-contain shrink-0" />
            <div>
              <div className="text-cyan-400 text-[11px] tracking-[0.18em] uppercase font-bold">Central Coast Fencing Industries</div>
              <h1 className="font-display text-xl sm:text-2xl font-semibold mt-1" data-testid="portal-client-name">
                Welcome, {client.name}
              </h1>
              {client.customer_number && <div className="text-slate-400 text-xs font-mono mt-0.5">Customer {client.customer_number}</div>}
            </div>
          </div>
          <div className="bg-cyan-600 text-white font-bold px-3 py-2 text-sm shrink-0">CCFI</div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-8 space-y-8">
        <section>
          <div className="flex items-center gap-2 mb-3">
            <FileText size={16} className="text-cyan-600" />
            <h2 className="font-display font-semibold text-slate-900">Your quotes</h2>
          </div>
          {quotes.length === 0 ? (
            <Card className="rounded-sm border-dashed border-2 border-slate-200 p-8 text-center text-sm text-slate-500" data-testid="portal-no-quotes">
              No quotes yet — your quote will appear here as soon as it's ready.
            </Card>
          ) : (
            <div className="space-y-3">
              {quotes.map((q) => (
                <a key={q.id} href={`${q.url}`} className="block group" data-testid={`portal-quote-${q.quote_number}`}>
                  <Card className="rounded-sm border-slate-200 p-4 flex items-center justify-between gap-3 group-hover:border-cyan-400 transition-colors">
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-900 text-sm">
                        {q.quote_number}{q.revision >= 2 ? <span className="text-slate-500 font-normal"> · Rev {q.revision}</span> : null}
                      </div>
                      <div className="text-xs text-slate-500 truncate">{q.site_address || "—"}</div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      {q.sale_price != null && <span className="font-mono font-semibold text-slate-900 text-sm">{fmt(q.sale_price)}</span>}
                      {q.needs_action ? (
                        <Badge variant="outline" className="rounded-sm text-[10px] uppercase tracking-wider border-cyan-400 bg-cyan-50 text-cyan-800 font-bold" data-testid={`portal-quote-updated-${q.quote_number}`}>
                          Updated — action needed
                        </Badge>
                      ) : (
                        <Badge variant="outline" className={`rounded-sm text-[10px] uppercase tracking-wider ${STATUS_STYLE[q.status] || "bg-slate-50 text-slate-600 border-slate-300"}`}>
                          {q.status}
                        </Badge>
                      )}
                      <ChevronRight size={16} className="text-slate-400 group-hover:text-cyan-600" />
                    </div>
                  </Card>
                </a>
              ))}
            </div>
          )}
        </section>

        {designs.length > 0 && (
          <section>
            <div className="flex items-center gap-2 mb-3">
              <Palette size={16} className="text-cyan-600" />
              <h2 className="font-display font-semibold text-slate-900">Your designs</h2>
            </div>
            <div className="space-y-4">
              {designs.map((d) => (
                <DesignCard key={d.id} design={d} cid={cid} token={token} />
              ))}
            </div>
          </section>
        )}

        <section>
          <div className="flex items-center gap-2 mb-3">
            <HardHat size={16} className="text-cyan-600" />
            <h2 className="font-display font-semibold text-slate-900">Your jobs</h2>
          </div>
          {jobs.length === 0 ? (
            <Card className="rounded-sm border-dashed border-2 border-slate-200 p-8 text-center text-sm text-slate-500" data-testid="portal-no-jobs">
              No active jobs yet — once you accept a quote, your job's live progress shows here.
            </Card>
          ) : (
            <div className="space-y-3">
              {jobs.map((j, i) => (
                <Card key={i} className="rounded-sm border-slate-200 p-4" data-testid={`portal-job-${j.job_number}`}>
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-900 text-sm">{j.job_number} — {j.title}</div>
                      {j.current_stage && <div className="text-xs text-slate-500">Current stage: {j.current_stage}</div>}
                      {j.scheduled_start && (
                        <div className="text-xs text-cyan-700 font-semibold mt-0.5 flex items-center gap-1" data-testid={`portal-job-dates-${j.job_number}`}>
                          <CalendarDays size={12} className="shrink-0" />
                          Install scheduled: {fmtD(j.scheduled_start)} → {fmtD(j.scheduled_end || j.scheduled_start)}
                        </div>
                      )}
                    </div>
                    <span className="font-mono text-sm font-semibold text-cyan-700 shrink-0">{Math.round(j.progress_pct)}%</span>
                  </div>
                  <div className="h-2 bg-slate-200 rounded-sm overflow-hidden">
                    <div className="h-full bg-cyan-600 transition-all" style={{ width: `${j.progress_pct}%` }} />
                  </div>
                  {(j.steps || []).length > 0 && (
                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-5 gap-1" data-testid={`portal-job-steps-${j.job_number}`}>
                      {j.steps.map((s) => (
                        <div key={s.key}
                          className={`flex sm:flex-col items-center sm:items-start gap-1.5 sm:gap-1 border rounded-sm px-2 py-1.5 ${s.done ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-white"}`}
                          data-testid={`portal-step-${j.job_number}-${s.key}`}>
                          {s.done
                            ? <CheckCircle2 size={13} className="text-emerald-600 shrink-0" />
                            : <Circle size={13} className="text-slate-300 shrink-0" />}
                          <span className={`text-[10px] font-semibold leading-tight ${s.done ? "text-emerald-800" : "text-slate-500"}`}>{s.label}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {(j.photos || []).length > 0 && (
                    <div className="mt-3">
                      <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1.5">Progress photos from site</div>
                      <div className="flex flex-wrap gap-1.5" data-testid={`portal-job-photos-${j.job_number}`}>
                        {j.photos.map((p, pi) => (
                          <button key={pi} type="button" onClick={() => setLightbox(p)}
                            className="w-16 h-16 rounded-sm overflow-hidden border border-slate-200 hover:ring-2 hover:ring-cyan-400 transition-shadow"
                            data-testid={`portal-job-photo-${j.job_number}-${pi}`}>
                            <img src={p.url} alt={p.caption || p.stage || "Progress photo"} className="w-full h-full object-cover" />
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}
        </section>

        <footer className="text-center text-xs text-slate-500 space-y-1 pt-4 border-t border-slate-200">
          <div className="font-semibold text-slate-600">Central Coast Fencing Industries Pty Ltd</div>
          <div>8 Dignity Cres, West Gosford NSW 2250</div>
          <div className="flex items-center justify-center gap-4 pt-1">
            <a href="tel:0243241441" className="inline-flex items-center gap-1 text-cyan-700"><Phone size={12} /> 02 4324 1441</a>
            <a href="mailto:info@ccfencingindustries.com.au" className="inline-flex items-center gap-1 text-cyan-700"><Mail size={12} /> info@ccfencingindustries.com.au</a>
          </div>
        </footer>
      </main>

      {lightbox && (
        <div className="fixed inset-0 z-50 bg-slate-950/80 flex items-center justify-center p-4" onClick={() => setLightbox(null)} data-testid="portal-photo-lightbox">
          <div className="max-w-3xl w-full" onClick={(e) => e.stopPropagation()}>
            <img src={lightbox.url} alt={lightbox.caption || "Progress photo"} className="w-full max-h-[75vh] object-contain rounded-sm" />
            <div className="flex items-center justify-between gap-3 mt-2 text-white">
              <div className="text-sm">
                {lightbox.caption && <div className="font-medium">{lightbox.caption}</div>}
                {lightbox.stage && <div className="text-xs text-slate-300">{lightbox.stage}</div>}
              </div>
              <button type="button" onClick={() => setLightbox(null)} className="text-xs border border-slate-500 rounded-sm px-3 py-1.5 hover:bg-white/10" data-testid="portal-lightbox-close">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DesignCard({ design, cid, token }) {
  const [d, setD] = useState(design);
  const [ticked, setTicked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [view3D, setView3D] = useState(false);
  const approved = d.approval_status === "approved";
  const isLouvre = d.infill === "louvre";
  const defaultAngle = d.louvre_angle_client_preferred ?? d.louvre_angle_deg ?? 45;
  const [angle, setAngle] = useState(defaultAngle);
  const [angleSaving, setAngleSaving] = useState(false);
  const [angleDirty, setAngleDirty] = useState(false);
  const [arOpen, setArOpen] = useState(false);
  // 3D geometry payload — presence indicates the design has enough data for the 3D viewer.
  const has3D = !!(d.panel_3d || d.members_3d || (d.sketch_points_3d && d.sketch_points_3d.length >= 2));
  const glbUrl = has3D ? `${API_ROOT}/public/portal/blueprints/${d.id}/ar.glb?cid=${cid}&t=${token}${isLouvre ? `&louvre_angle=${Math.round(angle)}` : ""}` : null;

  const approve = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_ROOT}/public/designs/${d.id}/approve?cid=${cid}&t=${token}`,
        { approved: true });
      setD({ ...d, approval_status: "approved", approved_at: data.approved_at });
    } catch {
      alert("Could not record your approval — please try again or call CCFI.");
    } finally {
      setBusy(false);
    }
  };

  const saveAngle = async (nextAngle) => {
    if (approved) return;
    setAngleSaving(true);
    try {
      await axios.post(`${API_ROOT}/public/designs/${d.id}/louvre-angle?cid=${cid}&t=${token}`,
        { angle_deg: nextAngle });
      setD({ ...d, louvre_angle_client_preferred: nextAngle });
      setAngleDirty(false);
    } catch {
      // silent — user can retry via slider release
    } finally {
      setAngleSaving(false);
    }
  };

  return (
    <Card className="rounded-sm border-slate-200 p-4" data-testid={`portal-design-${d.id}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="font-semibold text-slate-900 text-sm">{d.label}</div>
          <div className="text-xs text-slate-500 mt-0.5">
            {d.material === "aluminium" ? "Aluminium" : "Steel"}{d.colour_name ? ` · ${d.colour_name}` : ""} · {(d.total_m || 0).toFixed(1)} m × {d.height_mm} mm{d.gates > 0 ? ` · ${d.gates} gate(s)` : ""}
          </div>
        </div>
        {approved ? (
          <Badge variant="outline" className="rounded-sm shrink-0 border-emerald-300 bg-emerald-50 text-emerald-700 text-[10px] uppercase tracking-wider">
            <CheckCircle2 size={11} className="mr-1" /> Approved
          </Badge>
        ) : (
          <Badge variant="outline" className="rounded-sm shrink-0 border-amber-300 bg-amber-50 text-amber-700 text-[10px] uppercase tracking-wider">Awaiting your approval</Badge>
        )}
      </div>
      <div className="flex items-center justify-end mb-2 gap-1">
        {has3D && (
          <button type="button" onClick={() => setArOpen(true)}
            className="mr-auto rounded-sm border border-cyan-300 bg-cyan-50 hover:bg-cyan-100 text-cyan-700 px-2.5 h-7 text-[11px] font-semibold flex items-center gap-1.5"
            data-testid={`portal-ar-btn-${d.id}`}
            title="Pin the 3D fence on your driveway using your phone camera">
            <Smartphone size={12} /> See it at my place
          </button>
        )}
        {has3D && (
          <div className="inline-flex rounded-sm border border-slate-200 overflow-hidden" data-testid={`portal-view-toggle-${d.id}`}>
            <button type="button" onClick={() => setView3D(false)}
              className={`px-2.5 h-7 text-[10px] uppercase tracking-wider font-medium flex items-center gap-1 ${!view3D ? "bg-slate-800 text-white" : "bg-white text-slate-500 hover:bg-slate-50"}`}
              data-testid={`portal-view-2d-${d.id}`}>
              <PencilLine size={11} /> 2D
            </button>
            <button type="button" onClick={() => setView3D(true)}
              className={`px-2.5 h-7 text-[10px] uppercase tracking-wider font-medium flex items-center gap-1 ${view3D ? "bg-violet-600 text-white" : "bg-white text-violet-700 hover:bg-slate-50"}`}
              data-testid={`portal-view-3d-${d.id}`}>
              <Box size={11} /> 3D
            </button>
          </div>
        )}
      </div>
      {view3D && has3D ? (
        <div data-testid={`portal-3d-view-${d.id}`}>
          <Blueprint3DView
            mode={d.mode || "panel"}
            panel={d.panel_3d ? { ...d.panel_3d, louvre_angle_deg: isLouvre ? angle : (d.panel_3d.louvre_angle_deg || 45), colour_hex: d.colour_hex } : undefined}
            settings={{ colour_hex: d.colour_hex, height_mm: d.height_mm, infill: d.infill,
              post_size_mm: 65, louvre_angle_deg: isLouvre ? angle : d.louvre_angle_deg }}
            points={d.sketch_points_3d || []}
            segments={d.sketch_segments_3d || []}
            members={d.members_3d || []}
            colourHex={d.colour_hex}
            height={360}
            testid={`portal-3d-${d.id}`}
          />
        </div>
      ) : (
        <DesignVisual structureType={d.structure_type} infill={d.infill} colourHex={d.colour_hex}
          heightMm={d.height_mm} totalM={d.total_m} meshApertureMm={d.mesh_aperture_mm}
          louvreAngleDeg={angle} bladeOrientation={d.blade_orientation}
          laserCutPreset={d.laser_cut_preset} laserCutUploadUrl={d.laser_cut_upload_url} />
      )}

      {isLouvre && !approved && (
        <div className="mt-3 border border-cyan-200 bg-cyan-50/40 rounded-sm p-3" data-testid={`portal-louvre-slider-${d.id}`}>
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="flex items-center gap-1.5">
              <Wind size={13} className="text-cyan-600" />
              <span className="text-xs font-semibold text-slate-800">Try the louvre angle</span>
            </div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1">
              <span className="font-mono text-cyan-700 font-bold" data-testid={`portal-louvre-angle-value-${d.id}`}>{Math.round(angle)}°</span>
              <button type="button" onClick={() => { setAngle(45); setAngleDirty(true); saveAngle(45); }}
                className="text-slate-400 hover:text-cyan-600" title="Reset to 45°" data-testid={`portal-louvre-reset-${d.id}`}>
                <RotateCcw size={11} />
              </button>
            </div>
          </div>
          <input
            type="range" min="0" max="80" step="1" value={angle}
            onChange={(e) => { setAngle(Number(e.target.value)); setAngleDirty(true); }}
            onMouseUp={() => angleDirty && saveAngle(angle)}
            onTouchEnd={() => angleDirty && saveAngle(angle)}
            className="w-full accent-cyan-600"
            data-testid={`portal-louvre-slider-input-${d.id}`}
          />
          <div className="flex items-center justify-between text-[10px] text-slate-500 mt-1 font-mono">
            <span>0° — Flat (privacy)</span>
            <span>{angle < 30 ? "Maximum privacy" : angle > 60 ? "Maximum airflow" : "Balanced"}</span>
            <span>80° — Open (airflow)</span>
          </div>
          {angleSaving && <div className="text-[10px] text-cyan-600 mt-1"><Loader2 size={9} className="inline animate-spin mr-1" /> Saving your preference…</div>}
          {!angleSaving && d.louvre_angle_client_preferred != null && (
            <div className="text-[10px] text-slate-500 mt-1">Your preferred angle is saved — CCFI will fabricate at this angle when you approve the design.</div>
          )}
        </div>
      )}

      {approved ? (
        <p className="text-[11px] text-emerald-700 mt-3">Design approved on {(d.approved_at || "").slice(0, 10)} — thank you! CCFI will proceed with fabrication.</p>
      ) : (
        <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-3">
          <label className="flex items-start gap-2 text-xs text-slate-600 cursor-pointer">
            <Checkbox checked={ticked} onCheckedChange={(v) => setTicked(!!v)} data-testid={`design-approve-check-${d.id}`} className="mt-0.5" />
            I approve this design and confirm the measurements are correct
          </label>
          <Button size="sm" disabled={!ticked || busy} onClick={approve}
            className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white sm:ml-auto shrink-0"
            data-testid={`design-approve-btn-${d.id}`}>
            {busy ? <Loader2 size={13} className="animate-spin mr-1" /> : <CheckCircle2 size={13} className="mr-1" />} Approve design
          </Button>
        </div>
      )}

      {glbUrl && (
        <PortalARModal
          open={arOpen}
          onClose={() => setArOpen(false)}
          glbUrl={glbUrl}
          designLabel={d.label}
          colourName={d.colour_name}
          materialLabel={d.material === "aluminium" ? "Aluminium" : "Steel"}
          heightMm={d.height_mm}
          totalM={d.total_m}
        />
      )}
    </Card>
  );
}
